// Scheda cliente: riepilogo di tutto quello fatto nel CRM, sezione → anno → pratiche (con gli allegati).
// Dati da GET /api/riepilogo-cliente/{id} (backend/routes/search.py).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ClipboardList, FileText, Loader2 } from 'lucide-react';

export default function RiepilogoCliente({ clientId, API }) {
  const navigate = useNavigate();
  const [sezioni, setSezioni] = useState(null);
  const [aperte, setAperte] = useState({});   // "sezione|anno" → aperto

  useEffect(() => {
    if (!clientId) return;
    setSezioni(null);
    setAperte({});
    axios.get(`${API}/riepilogo-cliente/${clientId}`)
      .then((r) => setSezioni(r.data.sezioni || []))
      .catch(() => setSezioni([]));
  }, [clientId, API]);

  const apriAllegato = async (a) => {
    const finestra = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}${a.url}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (finestra) finestra.location.href = url; else window.open(url, '_blank');
    } catch (_) {
      if (finestra) finestra.close();
      toast.error('Allegato non disponibile');
    }
  };

  const annoCorrente = String(new Date().getFullYear());
  const aperto = (s, a, i) => aperte[`${s}|${a}`] ?? (a === annoCorrente || i === 0);

  return (
    <div className="border-t pt-4" data-testid="client-riepilogo">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3 uppercase text-sm tracking-wide">
        <ClipboardList className="w-4 h-4 text-pink-600" /> Riepilogo
      </h3>
      {sezioni === null ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : sezioni.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-3">Nessuna pratica per questo cliente</p>
      ) : (
        <div className="space-y-3">
          {sezioni.map((s) => (
            <div key={s.sezione} className="rounded-lg border">
              <div className="px-3 py-2 bg-slate-50 rounded-t-lg text-sm font-semibold text-slate-800">{s.sezione}</div>
              {s.anni.map((a, i) => {
                const k = `${s.sezione}|${a.anno}`;
                const on = aperto(s.sezione, a.anno, i);
                return (
                  <div key={a.anno} className="border-t">
                    <button type="button" onClick={() => setAperte((x) => ({ ...x, [k]: !on }))}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                      <ChevronDown className={`w-4 h-4 transition-transform ${on ? '' : '-rotate-90'}`} />
                      <span className="font-medium">{a.anno}</span>
                      <span className="text-xs text-slate-400">({a.voci.length})</span>
                    </button>
                    {on && (
                      <ul className="pb-2">
                        {a.voci.map((v, j) => (
                          <li key={j} className="px-9 py-1.5 text-sm flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <button type="button" onClick={() => navigate(v.link)} className="font-medium text-slate-900 hover:underline text-left">
                              {v.titolo}
                            </button>
                            {v.dettaglio && <span className="text-xs text-slate-500">{v.dettaglio}</span>}
                            {v.stato && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">{v.stato}</span>}
                            {v.data && <span className="text-xs text-slate-400">{v.data}</span>}
                            {v.allegati.map((al, n) => (
                              <button key={n} type="button" onClick={() => apriAllegato(al)}
                                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                                <FileText className="w-3.5 h-3.5 text-red-600" /> {al.nome}
                              </button>
                            ))}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
